<template>
  <div>
    <v-alert v-if="!isLoggedIn" type="error"
      >Attention: You must be logged in to an account to continue creating a review</v-alert
    >
    <v-container class="card">
      <v-form @submit.prevent>
        <section v-if="currentStep === 0">
          <h3>Select a Medium</h3>
          <v-row class="align-center mt-5" justify="center">
            <v-btn-toggle v-model="input.medium" label="Category" :value="Object.values($mediums)" dense>
              <v-btn
                v-for="(mediumText, mediums) in $mediums"
                :key="mediums"
                :value="mediumText"
                :disabled="!isLoggedIn"
              >
                {{ userFriendlyMediumWord(mediumText) }}
              </v-btn>
            </v-btn-toggle>
          </v-row>
        </section>

        <div v-if="currentStep > 0 && workDetails.posterSource" id="workDetails">
          <v-img
            :src="workDetails.posterSource"
            :max-height="$vuetify.breakpoint.xs ? 210 : 300"
            :max-width="$vuetify.breakpoint.xs ? 140 : 200"
            :class="$vuetify.breakpoint.xs ? 'mr-5' : 'mr-14'"
            contain
          />
          <div id="details">
            <br />
            <h1>{{ workDetails.title }}</h1>
            <br />
            <!-- Need to implement for other mediums -->
            <h2 v-for="creator in input.medium === 'film' ? workDetails.creators : []" :key="creator.id">
              {{ creator.name }}
            </h2>
            <rating-circle
              v-if="input.rating && !input.noRating && currentStep > 1"
              :score="input.rating"
              class="ratingSection mt-10"
            />
          </div>
        </div>

        <section v-if="currentStep === 1" id="workSearch">
          <media-search :medium="input.medium" @workSelected="selectWork($event)"></media-search>
        </section>

        <section v-if="currentStep === 2">
          <v-row justify="center">
            <v-rating
              v-model="input.rating"
              background-color="primaryText"
              color="secondary"
              hover
              length="10"
              :size="reactiveRatingSize"
              :disable="input.noRating"
              :dense="$vuetify.breakpoint.xs ? 'dense' : false"
            />
          </v-row>
        </section>
        <section v-if="currentStep === 3">
          <v-col>
            <v-row>
              <v-textarea v-model="input.review" class="pa-1" label="Review" auto-grow color="primaryText" />
            </v-row>
            <v-checkbox
              v-model="input.spoiler"
              label="This review contains spoilers"
              color="primaryText"
              true-value="1"
              false-value="0"
              hide-details
            />
          </v-col>
        </section>
        <section v-if="currentStep === 4">
          <v-row>
            <menu-date-picker v-model="input.date" :disabled="input.noDate" :max="nowIso"></menu-date-picker>
            <v-checkbox v-model="input.noDate" label="No Date" class="mt-4" color="primaryText" />
          </v-row>
        </section>
      </v-form>
      <div id="formNav" class="mt-9">
        <v-btn v-if="currentStep > 0" @click="backstep"> Previous </v-btn>
        <br />
        <v-btn v-if="currentStep < 4" :disabled="nextDisabled" color="primary" @click="currentStep++"> Next </v-btn>
        <v-btn
          v-if="currentStep === 4"
          :disabled="input.workID === '' || input.medium === '' || submitLoading || !isLoggedIn"
          color="primary"
          @click="submitForm"
          :loading="submitLoading"
        >
          submit
        </v-btn>
      </div>
    </v-container>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import mediaSearch from "../components/media-search.vue";
import RatingCircle from "../components/rating-circles/rating-circle-legacy.vue";
import MenuDatePicker from "../components/menu-date-picker.vue";
import { userFriendlyMediumWord } from "@/util/mediumHelpers";
import { toLocaleISO } from "../util/dateHelpers";

export default {
  name: "RatePage",

  components: { RatingCircle, mediaSearch, MenuDatePicker },

  data() {
    return {
      input: {
        medium: "",
        workID: "",
        title: "",
        rating: 5,
        review: undefined,
        date: "",
        noRating: false,
        noDate: false,
        spoiler: 0,
      },
      currentStep: 0,
      workDetails: {
        creators: [],
        posterSource: "",
        title: "",
      },
      results: [],
      tickLabels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      searchAllowed: true,
      menu: false,
      submitLoading: false,
    };
  },

  computed: {
    nextDisabled() {
      if (!this.isLoggedIn) {
        return true;
      }
      switch (this.currentStep) {
        case 0:
          return this.input.medium === "";
        case 1:
          return this.input.workID === "";
        default:
          return false;
      }
    },
    reactiveRatingSize() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 27;
        case "sm":
          return 40;
        default:
          return 45;
      }
    },
    nowIso() {
      return this.toLocaleISO(new Date());
    },

    isLoggedIn() {
      return !!this.$store.state.userId;
    },
  },

  mounted: function () {
    this.populateForm();
  },

  methods: {
    toLocaleISO,
    ...mapMutations(["showSnackbar"]),

    populateForm: async function () {
      this.input.date = this.toLocaleISO(new Date());
      if (this.$route.query.medium) {
        this.input.medium = this.$route.query.medium;
        this.currentStep++;
        if (this.$route.query.workID) {
          this.input.workID = this.$route.query.workID;
          await this.getDetailsFromId();
          this.currentStep++;
        }
      }
    },

    backstep: function () {
      if (this.currentStep === 0) return;
      else if (this.currentStep === 1) {
        this.input = {
          medium: "",
          workID: "",
          title: "",
          rating: 5,
          review: undefined,
          date: "",
          noRating: false,
          noDate: false,
          spoiler: 0,
        };
        this.workDetails = {
          creators: [],
          posterSource: "",
          title: "",
        };
      }
      this.currentStep--;
    },

    selectWork: function (item) {
      this.input.workID = item.id;
      this.input.title = item.title;
      this.workDetails.title = item.title;
      this.results = [];
      this.getDetailsFromId();
    },

    getDetailsFromId: async function () {
      const workDetailsResponse = await this.$axios.get(`/v1/works/${this.input.medium}/${this.input.workID}`);
      this.input.title = workDetailsResponse.data.title;
      this.workDetails.title = this.input.title;
      this.workDetails.posterSource = workDetailsResponse.data.posterSource;
      // TODO: Fill in more information like creators as needed
    },

    submitForm: async function () {
      this.submitLoading = true;
      if (this.input.noRating) this.input.ratingid = undefined;
      if (this.input.noDate) this.input.date = undefined;
      if (this.input.review === "") this.input.review = null;
      try {
        await this.$axios.post("/v1/reviews", {
          work: {
            id: this.input.workID.toString(),
            medium: this.input.medium,
          },
          review: this.input.review,
          rating: this.input.rating,
          spoiler: !!this.input.spoiler,
          dateViewed: this.input.date,
        });
        this.$router.push({ name: "home" });
      } catch (err) {
        this.showSnackbar({
          text: "A Server error occurred",
          error: true,
        });
      } finally {
        this.submitLoading = false;
        this.$store.commit("updateSeen", {
          medium: this.input.medium,
          workId: this.input.workID.toString(),
          value: true,
        });
      }
    },

    userFriendlyMediumWord,
  },
};
</script>

<style>
@import "~@ditdot-dev/vue-flow-form/dist/vue-flow-form.css";
@import "~@ditdot-dev/vue-flow-form/dist/vue-flow-form.theme-minimal.css";

#workDetails {
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
}
#details {
  display: inline-block;
  text-align: center;
}
#formNav {
  display: flex;
  justify-content: center;
}
</style>
